<template>
    <div class="mobile-app-shell" :class="{ 'has-tabs': showTabs }">
        <header class="shell-header">
            <div class="shell-header-inner">
                <h1 class="shell-title">{{ title }}</h1>
                <button v-if="showLogout" type="button" class="btn-logout" @click="$emit('logout')">
                    <i class="fa fa-sign-out-alt"></i>
                </button>
            </div>
        </header>
        <main class="shell-body">
            <slot></slot>
        </main>
        <nav v-if="showTabs" class="shell-tabs" aria-label="주요 메뉴">
            <button
                v-for="tab in tabs"
                :key="tab.id"
                type="button"
                class="shell-tab"
                :class="{ active: activeTab === tab.id }"
                @click="$emit('tab-change', tab.id)"
            >
                <i class="fa" :class="tab.icon"></i>
                <span>{{ tab.label }}</span>
            </button>
        </nav>
    </div>
</template>

<script>
export default {
    name: "MobileAppShell",
    props: {
        title: {
            type: String,
            default: "",
        },
        showLogout: {
            type: Boolean,
            default: true,
        },
        tabs: {
            type: Array,
            default: () => [],
        },
        activeTab: {
            type: String,
            default: "",
        },
    },
    computed: {
        showTabs() {
            return Array.isArray(this.tabs) && this.tabs.length > 0;
        },
    },
};
</script>

<style scoped>
/*
 * A안: 헤더·탭바 높이를 CSS 변수로 단일화.
 * - safe-area 는 각 크롬 높이 안에만 포함 (자식 sticky/padding 에서 재가산 금지)
 * - 슬롯 콘텐츠(ctx-bar 등)는 상속된 --shell-header-height 를 top 으로 사용
 */
.mobile-app-shell {
    --shell-header-pad-top: max(12px, env(safe-area-inset-top, 0px));
    --shell-header-pad-bottom: 12px;
    --shell-header-title-line: 1.375rem; /* 1.1rem × line-height 1.25 */
    --shell-header-height: calc(
        var(--shell-header-pad-top) + var(--shell-header-pad-bottom) + var(--shell-header-title-line)
    );

    --shell-tab-pad-top: 6px;
    --shell-tab-pad-bottom: calc(6px + env(safe-area-inset-bottom, 0px));
    --shell-tab-item-min: 44px;
    --shell-tabbar-height: calc(
        var(--shell-tab-pad-top) + var(--shell-tab-item-min) + var(--shell-tab-pad-bottom)
    );

    min-height: 100vh;
    background: #f0f2f5;
    display: flex;
    flex-direction: column;
}

.shell-header {
    background: linear-gradient(135deg, #2e579c 0%, #1e3a6e 100%);
    color: #fff;
    padding: var(--shell-header-pad-top) 16px var(--shell-header-pad-bottom);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    position: sticky;
    top: 0;
    z-index: 100;
}

.shell-header-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 480px;
    margin: 0 auto;
    min-height: var(--shell-header-title-line);
}

.shell-title {
    font-size: 1.1rem;
    line-height: 1.25;
    font-weight: 600;
    margin: 0;
}

.btn-logout {
    background: rgba(255, 255, 255, 0.15);
    border: none;
    color: #fff;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 1rem;
    line-height: 1;
}

.shell-body {
    flex: 1;
    max-width: 480px;
    width: 100%;
    margin: 0 auto;
    padding: 12px 16px 24px;
    padding-bottom: max(24px, env(safe-area-inset-bottom, 0px));
}

.has-tabs .shell-body {
    /* 탭바 실높이와 동일 — safe-area 재가산 없음 */
    padding-bottom: var(--shell-tabbar-height);
}

.shell-tabs {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 110;
    display: flex;
    max-width: 480px;
    margin: 0 auto;
    padding: var(--shell-tab-pad-top) 4px var(--shell-tab-pad-bottom);
    background: #fff;
    border-top: 1px solid #e2e6ed;
    box-shadow: 0 -4px 16px rgba(26, 31, 39, 0.06);
}

/* 480px 폭 제한 시에도 탭바 배경이 뷰포트 전체에 이어지도록 */
.shell-tabs::before {
    content: "";
    position: absolute;
    left: 50%;
    bottom: 0;
    top: 0;
    width: 100vw;
    transform: translateX(-50%);
    background: #fff;
    border-top: 1px solid #e2e6ed;
    box-shadow: 0 -4px 16px rgba(26, 31, 39, 0.06);
    z-index: 0;
    pointer-events: none;
}

.shell-tab {
    position: relative;
    z-index: 1;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    min-height: var(--shell-tab-item-min);
    padding: 4px 2px;
    border: none;
    background: transparent;
    color: #6b7484;
    font-size: 0.72rem;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}

.shell-tab i {
    font-size: 1.15rem;
    line-height: 1;
}

.shell-tab.active {
    color: #2e579c;
}

.shell-tab.active i {
    font-weight: 700;
}
</style>
